import * as React from "react"
import { toast } from "sonner"

import { ReportExportType } from "@ibada/shared-types/reporting"

import { useTranslations } from "@/i18n"
import { buildReportingScopeDefinition } from "@/features/reports/hooks/use-report-builder"
import { useReportsRouteScope } from "@/features/reports/hooks/use-reports-route-scope"
import { reportsApi } from "@/features/reports/services/reports-api"

import type { AccountingJournalQuery } from "@/features/finance/model/accounting-reports-types"
import {
  JOURNAL_EXPORT_MAX_ENTRIES,
  fetchAllJournalEntries,
} from "@/features/reports/lib/journal-export"
import { downloadBlob, downloadTextFile, reportToCsv } from "@/features/reports/lib/report-csv"
import { buildReportPdfPayload } from "@/features/reports/lib/report-pdf-payload"
import type { ReportConfigValues } from "@/features/reports/lib/report-config-url"
import { useResolvedPeriod } from "@/features/reports/lib/use-resolved-period"
import type { NormalizedReport } from "@/features/reports/model/normalized-report"
import {
  AccountingEndpoint,
  ReportExportKind,
  type FinanceReportEntry,
} from "@/features/reports/model/report-registry-types"
import { adaptJournal } from "@/features/reports/services/adapters/accounting-adapters"
import { useAccountingLabels } from "@/features/reports/services/adapters/use-accounting-labels"
import { useFinanceReportScope } from "@/features/reports/services/use-finance-report-scope"
import { AnalyticsEvents, captureEvent } from "@/shared/lib/analytics/analytics"

export interface ReportExporter {
  /** Formats offered for this report; empty → export is unavailable (Print still works). */
  formats: ReportExportType[]
  exportAs: (format: ReportExportType) => void
  isExporting: boolean
}

/** Engine reports export via the ADR-0013 (Reporting Platform Foundation) platform (PDF/Excel/CSV); accounting tables get a client-built CSV. */
export function useReportExport(
  entry: FinanceReportEntry,
  report: NormalizedReport | undefined,
  currency: string,
  config: ReportConfigValues = {},
): ReportExporter {
  const t = useTranslations()
  const scope = useReportsRouteScope()
  const financeScope = useFinanceReportScope()
  const period = useResolvedPeriod(config.period)
  const accountingLabels = useAccountingLabels()
  const [isExporting, setIsExporting] = React.useState(false)

  const kind = entry.export.kind
  const source = entry.dataSource

  /*
   * A template report can always produce a PDF now: the surface has already formatted every cell, so the
   * server only has to draw it. Before this, the only offer on a template report was CSV — and the toolbar
   * fell back to the browser's Print dialog, which produces a screenshot of a web page, not a document.
   */
  const formats = React.useMemo<ReportExportType[]>(() => {
    // Exports are disclosures of report data, not a local presentation preference. The same central
    // finance-view decision that enables this report must therefore also be true before the toolbar can
    // offer a download or its handler can request one.
    if (!financeScope.canView) return []
    if (kind === ReportExportKind.Engine) return [ReportExportType.PDF, ReportExportType.EXCEL, ReportExportType.CSV]
    if (kind === ReportExportKind.ClientCsv) return [ReportExportType.PDF, ReportExportType.CSV]
    // Statements render their own layout, so there are no columns to hand the table renderer.
    return []
  }, [financeScope.canView, kind])

  const fileBase = `${entry.id}-${new Date().toISOString().slice(0, 10)}`

  // The journal is the one accounting report the screen shows a single PAGE of, so the on-screen rows are not
  // the report — exporting them would hand a treasurer the first 50 postings labelled "the general journal".
  // The export re-fetches the whole thing under the SAME scope + period the view uses.
  const isJournal = source.kind === "accounting" && source.endpoint === AccountingEndpoint.Journal
  const journalQuery = React.useMemo<AccountingJournalQuery | null>(() => {
    if (!isJournal || !financeScope.workspaceId) return null
    return {
      workspaceId: financeScope.workspaceId,
      branchId: financeScope.branchId,
      scope: financeScope.scope,
      dateFrom: period.from,
      dateTo: period.to,
    }
  }, [isJournal, financeScope.workspaceId, financeScope.branchId, financeScope.scope, period.from, period.to])

  const exportAs = React.useCallback(
    (format: ReportExportType) => {
      if (!financeScope.canView) return
      void (async () => {
        setIsExporting(true)
        // Fired on INTENT, not on completion: a treasurer who asks for a 40MB Excel and gives up waiting is
        // the case worth seeing, and a success-only event hides exactly that. `format` and `report_id` are
        // enums/catalog keys — never the report's contents.
        captureEvent(AnalyticsEvents.ReportExported, {
          report_id: entry.id,
          report_module: entry.module,
          export_format: format,
          export_kind: kind,
        })
        try {
          if (kind === ReportExportKind.Engine && source.kind === "engine-template" && scope) {
            const definition = await reportsApi.resolveTemplateDefinition({
              templateId: source.templateId,
              scope: buildReportingScopeDefinition(scope),
              inputs: undefined,
            })
            const result = await reportsApi.exportReport({ definition, exportType: format, fileName: fileBase })
            downloadBlob(result.blob, result.fileName ?? `${fileBase}.${extensionFor(format)}`)
            return
          }

          /*
           * The journal is the one report the screen shows a single PAGE of, so the visible rows are not
           * the report. Both formats re-fetch the whole thing under the SAME scope + period the view uses.
           */
          const table = journalQuery
            ? await (async () => {
                const journal = await fetchAllJournalEntries(journalQuery)
                // Hit the hard stop → say so. A file that is silently short is the bug this replaced.
                if (!journal.complete) {
                  toast.error(
                    t("finance.reports.export.tooLarge", { total: journal.total, max: JOURNAL_EXPORT_MAX_ENTRIES }),
                  )
                  return null
                }
                return adaptJournal(journal.entries, accountingLabels)
              })()
            : report

          if (!table) return

          /*
           * The format the caller ASKED for, or an error — never a quiet substitution.
           *
           * The journal branch used to run before the format was looked at, so asking for a PDF wrote a CSV
           * and named it one. Downloading the wrong format silently is worse than refusing.
           */
          if (format === ReportExportType.PDF) {
            if (!financeScope.workspaceId) {
              toast.error(t("finance.reports.export.failed" as never))
              return
            }
            const blob = await reportsApi.renderTemplatePdf({
              workspaceId: financeScope.workspaceId,
              branchId: financeScope.branchId,
              ...buildReportPdfPayload({
                report: table,
                title: entry.titleKey ? (t(entry.titleKey as never) as string) : entry.id,
                resolveLabel: (labelKey) => t(labelKey as never) as string,
                currency,
                filters:
                  period.from && period.to
                    ? [
                        {
                          label: t("finance.reports.toolbar.period" as never) as string,
                          value: `${period.from} — ${period.to}`,
                        },
                      ]
                    : undefined,
              }),
            })
            downloadBlob(blob, `${fileBase}.pdf`)
            return
          }

          downloadTextFile(`${fileBase}.csv`, reportToCsv(table, (labelKey) => t(labelKey as never), currency))
        } catch {
          // A failed export must never look like a successful one.
          toast.error(t("finance.reports.export.failed" as never))
        } finally {
          setIsExporting(false)
        }
      })()
    },
    [kind, source, scope, journalQuery, accountingLabels, report, currency, fileBase, t, entry, financeScope, period],
  )

  return { formats, exportAs, isExporting }
}

function extensionFor(format: ReportExportType): string {
  if (format === ReportExportType.EXCEL) return "xlsx"
  if (format === ReportExportType.PDF) return "pdf"
  return "csv"
}
